import type { Building } from "building-types/building/buildingTypes";
import type { ValuesOf } from "common/utils/types";
import type { TierResult } from "detect-gpu";
import { useEffect } from "react";
import { useShowroomBasePlanDataFetchKey } from "../data/useShowroomAPI";

/**
 * Get the entity count from a building for performance tracking.
 * Used to calculate entities per second for CPU performance evaluation.
 */
export function getBuildingEntityCount(building: Building | null | undefined): number {
  return building ? Object.keys(building.rooms).length : 0;
}

type ShowroomPerformanceId = "showroomIntro";

const showroomPerformanceEvents = [
  "timeTo3dInteractive", // From load to onInitialBuildingRendered
  "getPlanData", // From app params ready to response received
  "docReady", // From load to Showroom doc state ready
  "buildingReady", // From load to building or external artifact ready for the 3D viewer
  "generateBuilding", // Generate building (main thread)
  "generateFloorPlanPayload", // Generate precomputed 2D floor-plan payload (main thread)
  "floorPlanReady", // Readiness timestamp; live 2D also reports lag from initial 3D render
  "render3dViewer", // From building ready to onInitialBuildingRendered
  "optionsReady", // From building ready to options visible
  // Worker mode events (durations reported from worker thread)
  "workerBuildingGeneration", // Building generation time in worker
  "workerGeometryCollection", // Geometry collection time in worker
  "workerRenderPayloadAssembly", // Render payload assembly time in worker
  "gltfParsing", // Server GLTF parsing time on main thread
  // Server mode events (durations reported from server)
  "serverBuildingGeneration", // Building generation time in server
] as const;
export type ShowroomPerformanceEvent = ValuesOf<typeof showroomPerformanceEvents>;

type PerformanceTiming = {
  startTimeMs?: number;
  durationMs: number;
};

type PerformanceReport = Record<ShowroomPerformanceEvent, number>;

export interface PerformanceFinalizedData {
  entitiesPerSecond: number;
  entityCount: number;
  cpuSlow: boolean;
  fpsSlow: boolean;
  degradeUILevel: number;
  overrideReason?: string;
  finalized: boolean;
}

export class ShowroomPerformance {
  id: ShowroomPerformanceId;
  eventsCalled: Set<string>;
  manualTimings: Partial<Record<ShowroomPerformanceEvent, PerformanceTiming>> = {};
  gpuInfo: TierResult | null = null;
  previousFetchKey: string | undefined = undefined;
  performanceFinalized: boolean = false;
  performanceHadGpuOnFirstCheck: boolean = false;
  finalizedData: PerformanceFinalizedData | null = null;
  eventFired: boolean = false;

  constructor(id: ShowroomPerformanceId) {
    this.id = id;
    // Ensures events are only called once
    this.eventsCalled = new Set();
  }

  private event(event: ShowroomPerformanceEvent) {
    return `${this.id}-${event}`;
  }

  private startMark(event: ShowroomPerformanceEvent) {
    return `start-${this.event(event)}`;
  }
  private endMark(event: ShowroomPerformanceEvent) {
    return `end-${this.event(event)}`;
  }
  private measureName(event: ShowroomPerformanceEvent) {
    return `measure-${this.event(event)}`;
  }

  private mark(mark: string) {
    if (!this.eventsCalled.has(mark)) {
      performance.mark(mark);
      this.eventsCalled.add(mark);
    }
  }

  start(event: ShowroomPerformanceEvent) {
    this.mark(this.startMark(event));
  }

  measure(event: ShowroomPerformanceEvent) {
    const measure = this.measureName(event);
    if (!this.eventsCalled.has(measure)) {
      const start = performance.getEntriesByName(this.startMark(event));
      const end = performance.getEntriesByName(this.endMark(event));
      if (!start.length || !end.length) {
        return;
      }

      performance.measure(measure, this.startMark(event), this.endMark(event));
      this.eventsCalled.add(measure);

      performance.clearMarks(this.startMark(event));
      performance.clearMarks(this.endMark(event));
    }
  }

  end(event: ShowroomPerformanceEvent) {
    this.mark(this.endMark(event));
    this.measure(event);
  }

  getDuration(event: ShowroomPerformanceEvent): number | null {
    const manualTiming = this.manualTimings[event];
    if (manualTiming) {
      return manualTiming.durationMs;
    }
    const entries = performance.getEntriesByName(this.measureName(event));
    return entries.length > 0 ? entries[0].duration : null;
  }

  /**
   * Record a pre-computed timing for an event.
   * Use this for timing from worker threads where we can't use mark/measure APIs.
   * @param event - The performance event to record
   * @param timing - The start time and duration in milliseconds
   */
  recordTiming(event: ShowroomPerformanceEvent, timing: PerformanceTiming): void {
    const measureName = this.measureName(event);
    // Clear any existing measure for this event before recording new one
    performance.clearMeasures(measureName);
    this.manualTimings[event] = timing;
    const start = timing.startTimeMs !== undefined ? timing.startTimeMs - performance.timeOrigin : undefined;
    if (start !== undefined && Number.isFinite(start) && Number.isFinite(timing.durationMs)) {
      try {
        performance.measure(measureName, { start, duration: timing.durationMs });
      } catch {
        // performance.measure options may not be supported in all environments
      }
    }
  }

  report() {
    const performanceReport = {} as PerformanceReport & Partial<PerformanceFinalizedData>;

    for (const event of showroomPerformanceEvents) {
      const manualTiming = this.manualTimings[event];
      if (manualTiming) {
        performanceReport[event] = manualTiming.durationMs;
        delete this.manualTimings[event];
        continue;
      }

      const entries = performance.getEntriesByName(this.measureName(event));
      for (const entry of entries) {
        // Must be numeric for PostHog
        performanceReport[event] = entry.duration;
        performance.clearMeasures(entry.name);
      }
    }

    // Include finalized data if available
    if (this.finalizedData) {
      Object.assign(performanceReport, this.finalizedData);
    }

    return performanceReport;
  }

  reset() {
    this.eventsCalled.clear();
    this.manualTimings = {};
    // note: we don't reset gpuInfo here -- want it to persist across component lifecycles
  }

  resetFinalizedState(): void {
    this.performanceFinalized = false;
    this.performanceHadGpuOnFirstCheck = false;
    this.finalizedData = null;
    this.eventFired = false;
  }
}

// TODO: will need init this with showroomVersion (derived from url) when we support different versions
// Defaulting to showroomIntro for now
const showroomPerformanceClass = new ShowroomPerformance("showroomIntro");

/**
 * Access the showroom performance singleton via callback.
 *
 * `requires` is a truthiness gate, not a React deps array — it gates execution, it does
 * not detect changes. Edge-triggered call sites work only because `mark()` is idempotent
 * at the singleton level. For simple property access use `.get()` / `.set()` instead.
 *
 * @example showroomPerformance((perf) => perf.set("gpuInfo", info), [info])
 */
export function showroomPerformance(callback: (perf: ShowroomPerformance) => void, requires: unknown[] = []) {
  if (callback && typeof window !== "undefined" && (!requires.length || requires.every((value) => !!value))) {
    callback(showroomPerformanceClass);
  }
}

// Getter and setter: helpers for one-line reads/writes when you don't need the callback pattern

/**
 * Get a specific property. Use callback pattern for methods/multi-property access/deps.
 * @example showroomPerformance.get("gpuInfo")
 */
showroomPerformance.get = <K extends keyof ShowroomPerformance>(key: K): ShowroomPerformance[K] | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return showroomPerformanceClass[key];
};

/**
 * Set a specific property. Use callback pattern for multi-property writes or deps.
 * @example showroomPerformance.set("gpuInfo", gpuInfo)
 */
showroomPerformance.set = <K extends keyof ShowroomPerformance>(key: K, value: ShowroomPerformance[K]): void => {
  if (typeof window !== "undefined") {
    showroomPerformanceClass[key] = value;
  }
};

// When the fetch key changes (opened a new plan), reset performance class
export function useInitShowroomPerformance() {
  const fetchKey = useShowroomBasePlanDataFetchKey();
  useEffect(() => {
    if (showroomPerformanceClass.previousFetchKey && showroomPerformanceClass.previousFetchKey !== fetchKey) {
      showroomPerformanceClass.reset();
      showroomPerformanceClass.resetFinalizedState();
    }
    showroomPerformanceClass.previousFetchKey = fetchKey; // stored on the class to persist across component lifecycles
  }, [fetchKey]);
}
